<template>
  <Expandable header="Hospital Address" class="mb-10">
    <b-row class="pb-0">
      <b-col cols="12" md="4">
        <input-field
          v-model.trim="form.address_line_1"
          label="Address Line 1"
          @input="input"
          :disabled="isDisabled"
          rules="required|max-100"
        ></input-field>
      </b-col>
      <b-col cols="12" md="4">
        <input-field
          v-model.trim="form.address_line_2"
          label="Address Line 2"
          :disabled="isDisabled"
          rules="max-100"
          @input="input"
        ></input-field>
      </b-col>
      <b-col cols="12" md="4">
        <input-field
          v-model.trim="form.town_village"
          label="Town Village"
          @input="input"
          :disabled="isDisabled"
          rules="required|max-100"
        ></input-field>
      </b-col>
      <b-col cols="12" md="4">
        <input-field
          v-model.trim="form.landmark"
          label="Landmark"
          @input="input"
          rules="max-100"
          :disabled="isDisabled"
        ></input-field>
      </b-col>
      <b-col cols="12" md="4">
        <autocomplete
          v-model.trim="form.country"
          @input="onCountryChange"
          label="Country"
          :items="countries"
          :disabled="isDisabled"
          rules="required"
        ></autocomplete>
      </b-col>
      <b-col cols="12" md="4">
        <autocomplete
          v-model.trim="form.state"
          label="State"
          @input="onStateChange"
          :disabled="isDisabled"
          :items="states"
          rules="required"
        ></autocomplete>
      </b-col>
      <!-- Phase 1 Corrections - City renamed to District, taluks load based on this -->
      <b-col cols="12" md="4">
        <autocomplete
          v-model.trim="form.city"
          :disabled="isDisabled"
          :items="citys"
          @input="onDistrictChange"
          label="District"
          rules="required"
        ></autocomplete>
      </b-col>
      <!-- Phase 1 Corrections - Taluk (loads based on District/City selection) -->
      <b-col cols="12" md="4">
        <autocomplete
          v-model.trim="form.taluk_id"
          :disabled="isDisabled"
          :items="taluks"
          @input="input"
          label="Taluk"
          rules="required"
        ></autocomplete>
      </b-col>
      <b-col cols="12" md="4">
        <input-field
          @input="input"
          v-model.trim="form.pincode"
          :disabled="isDisabled"
          label="Pincode"
          type="number"
          rules="required|zipcode|integer"
        ></input-field>
      </b-col>
      <b-col cols="12" md="4">
        <input-field
          @input="onLatLngManualInput"
          v-model.trim="form.latitude"
          :disabled="isDisabled"
          label="Latitude"
          type="text"
          placeholder="e.g. 12.9716"
          rules="decimal"
        ></input-field>
      </b-col>
      <b-col cols="12" md="4">
        <input-field
          @input="onLatLngManualInput"
          v-model.trim="form.longitude"
          :disabled="isDisabled"
          label="Longitude"
          type="text"
          placeholder="e.g. 77.5946"
          rules="decimal"
        ></input-field>
      </b-col>
      <b-col cols="12" md="4" class="d-flex align-items-center pt-2">
        <b-button
          variant="primary"
          size="sm"
          :disabled="isDisabled || locating"
          @click="getCurrentLocation"
        >
          <b-spinner small v-if="locating" class="mr-1"></b-spinner>
          {{ locating ? "Locating..." : "Get Current Location" }}
        </b-button>
        <b-button
          variant="outline-info"
          size="sm"
          class="ml-2"
          :disabled="isDisabled || geocoding"
          @click="locateFromAddress"
        >
          <b-spinner small v-if="geocoding" class="mr-1"></b-spinner>
          {{ geocoding ? "Searching..." : "Locate from Address" }}
        </b-button>
      </b-col>
    </b-row>
    <b-alert
      v-if="locationError"
      show
      variant="warning"
      class="mt-2 py-1 px-2"
      dismissible
      @dismissed="locationError = ''"
    >
      <small>{{ locationError }}</small>
    </b-alert>
    <!-- Leaflet OpenStreetMap Picker - shown only after locate action -->
    <div v-if="showMap" class="mt-3">
      <div ref="mapContainer" class="leaflet-map-container"></div>
      <small class="text-muted">Click on the map to set hospital location. Drag the marker to adjust.</small>
    </div>
  </Expandable>
</template>

<script>
import {
  FIELD,
  getMaxNotRules,
  getMaxRules,
  PINCODE,
} from "../../../assets/js/rules";
import { HOSPITAL_TYPES } from "../../../assets/constants/hospital.contants";
import masterService from "../../../assets/services/master.service";
import HospitalService from "../../../assets/services/hospital.service";
import Expandable from "../../Expandable.vue";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix Leaflet default marker icon path issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Nominatim geocoding (free, no API key)
const NOMINATIM_URL = "https://nominatim.openstreetmap.org/search";

export default {
  components: { Expandable },
  props: {
    value: Object,
    isDisabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      rules: FIELD,
      pincode: PINCODE,
      form: {
        address_line_1: "",
        address_line_2: "",
        town_village: "",
        is_permanent_address: 1,
        landmark: "",
        country: 99,
        state: 29,  // Karnataka LGD code
        city: "",   // This is now District (label changed)
        taluk_id: "",  // Phase 1 Corrections - Taluk ID
        pincode: "",
        latitude: "",
        longitude: "",
      },
      citys: [],
      states: [],
      taluks: [],  // Phase 1 Corrections
      isInitialized: false,  // Track if initial load is done
      locating: false,
      geocoding: false,
      locationError: "",
      showMap: false,
      map: null,
      marker: null,
    };
  },
  watch: {
    // Handle when parent updates value prop (edit mode data load)
    value: {
      async handler(newVal) {
        if (!this.isInitialized) return;
        // If country changed and states not loaded, reload them
        if (newVal.country && this.states.length === 0) {
          await this.onCountryChange(true);
        }
        // If state changed and cities not loaded, reload them
        if (newVal.state && this.citys.length === 0) {
          await this.onStateChange(true);
        }
        // If city changed and taluks not loaded, reload them
        if (newVal.city && this.taluks.length === 0) {
          await this.loadTaluks();
        }
        // Update map marker if lat/lng changed externally
        if (newVal.latitude && newVal.longitude) {
          this.updateMapMarker(
            parseFloat(newVal.latitude),
            parseFloat(newVal.longitude)
          );
        }
      },
      deep: true
    },
    // Watch form.country specifically to ensure states load (only after initialization)
    'form.country': {
      async handler(newVal) {
        if (!this.isInitialized) return;
        if (newVal && this.states.length === 0) {
          await this.onCountryChange(true);
        }
      }
    },
    // Watch form.state specifically to ensure districts load (only after initialization)
    'form.state': {
      async handler(newVal) {
        if (!this.isInitialized) return;
        if (newVal && this.citys.length === 0) {
          await this.onStateChange(true);
        }
      }
    },
    // Watch form.city specifically to ensure taluks load when district changes (only after initialization)
    'form.city': {
      async handler(newVal) {
        if (!this.isInitialized) return;
        if (newVal && this.taluks.length === 0) {
          await this.loadTaluks();
        }
      }
    }
  },
  computed: {
    types() {
      return HOSPITAL_TYPES;
    },
    master() {
      return this.$store.getters.master;
    },
    countries() {
      if (this.master.country instanceof Array == false) return [];
      return this.master.country.map((item) => {
        return { value: item.id, name: item.name };
      });
    },

    years() {
      let year = 1899;
      let list = [];
      while (year < new Date().getFullYear()) {
        year++;
        list.push(year);
      }
      return list.reverse();
    },
  },
  methods: {
    getMaxRules(max) {
      return getMaxRules(max);
    },
    getMaxNotRules(max) {
      return getMaxNotRules(max);
    },
    async onCountryChange(copy) {
      const vm = this;
      if (copy == undefined) {
        vm.form.state = "";
        vm.form.city = "";
        vm.form.taluk_id = "";
      }

      vm.states = [];
      vm.citys = [];
      vm.taluks = [];
      vm.$emit("input", vm.form);
      if (!vm.form.country) return;
      try {
        vm.$loader.show();
        let { data } = await masterService.getStateCity({
          country_id: vm.form.country,
        });
        let { list, status, msg } = data;
        if (status) {
          vm.states = list.map((item) => {
            return { name: item.name, value: item.id };
          });
        } else {
          vm.$alert.show(msg);
        }
      } catch (error) {
        console.error("Error loading states:", error);
      } finally {
        vm.$loader.hide();
      }
    },
    async onStateChange(copy) {
      const vm = this;
      if (copy == undefined) {
        vm.form.city = "";
        vm.form.taluk_id = "";
      }
      vm.citys = [];
      vm.taluks = [];
      if (!vm.form.state) return;
      vm.$emit("input", vm.form);
      try {
        vm.$loader.show();
        let { data } = await masterService.getStateCity({
          state_id: vm.form.state,
        });
        let { list, status, msg } = data;
        if (status) {
          vm.citys = list.map((item) => {
            return { name: item.name, value: item.id };
          });
        } else {
          vm.$alert.show(msg);
        }
      } catch (error) {
        console.error("Error loading districts:", error);
      } finally {
        vm.$loader.hide();
      }
    },
    // Phase 1 Corrections - Handle District (City) Change and load Taluks
    async onDistrictChange(copy) {
      const vm = this;
      if (copy == undefined) {
        vm.form.taluk_id = "";
      }
      vm.taluks = [];
      vm.$emit("input", vm.form);
      if (!vm.form.city) return;
      await vm.loadTaluks();
    },
    // Phase 1 Corrections - Load Taluks by District (City)
    async loadTaluks() {
      const vm = this;
      if (!vm.form.city) return;
      try {
        const response = await HospitalService.getTaluksByDistrict(vm.form.city);
        if (response.data && response.data.status && response.data.list) {
          vm.taluks = response.data.list.map(item => ({
            name: item.taluk_name,
            value: item.taluk_id
          }));
        }
      } catch (error) {
        console.error("Error loading taluks:", error);
      }
    },
    getCurrentLocation() {
      this.locating = true;
      this.locationError = "";
      if (!navigator.geolocation) {
        this.locationError = "Geolocation is not supported by this browser.";
        this.locating = false;
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          this.form.latitude = lat.toFixed(8);
          this.form.longitude = lng.toFixed(8);
          this.input();
          this.locating = false;
          this.openMap(lat, lng);
        },
        (error) => {
          this.locating = false;
          if (error.code === 1) {
            this.locationError =
              "Location access denied. Please enable location permissions.";
          } else if (error.code === 2) {
            this.locationError = "Unable to determine location.";
          } else {
            this.locationError = "Location request timed out.";
          }
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    },
    // Geocode from entered address fields using Nominatim (free)
    async locateFromAddress() {
      const parts = [
        this.form.address_line_1,
        this.form.address_line_2,
        this.form.town_village,
        this.getSelectedName(this.citys, this.form.city),
        this.getSelectedName(this.states, this.form.state),
        this.form.pincode,
        "India",
      ].filter(Boolean);
      const address = parts.join(", ");
      if (!address || address === "India") {
        this.locationError = "Please enter address details first.";
        return;
      }
      this.geocoding = true;
      this.locationError = "";
      try {
        const url =
          NOMINATIM_URL +
          "?format=json&limit=1&q=" +
          encodeURIComponent(address);
        const response = await fetch(url, {
          headers: { "Accept-Language": "en" },
        });
        const results = await response.json();
        if (results && results.length > 0) {
          const lat = parseFloat(results[0].lat);
          const lng = parseFloat(results[0].lon);
          this.form.latitude = lat.toFixed(8);
          this.form.longitude = lng.toFixed(8);
          this.input();
          this.openMap(lat, lng);
        } else {
          this.locationError =
            "Could not find location for the entered address. Try clicking on the map instead.";
        }
      } catch (e) {
        this.locationError = "Geocoding request failed. Please try again.";
      } finally {
        this.geocoding = false;
      }
    },
    // Get display name from dropdown items by value
    getSelectedName(items, value) {
      if (!items || !value) return "";
      const item = items.find((i) => i.value == value);
      return item ? item.name : "";
    },
    // Show map and initialize if needed, then place marker
    openMap(lat, lng) {
      if (!this.showMap) {
        this.showMap = true;
        this.$nextTick(() => {
          this.initMap(lat, lng);
        });
      } else {
        this.updateMapMarker(lat, lng);
      }
    },
    // When user manually types lat/lng, update marker if map is open
    onLatLngManualInput() {
      this.input();
      if (!this.showMap) return;
      const lat = parseFloat(this.form.latitude);
      const lng = parseFloat(this.form.longitude);
      if (
        !isNaN(lat) &&
        !isNaN(lng) &&
        lat >= -90 &&
        lat <= 90 &&
        lng >= -180 &&
        lng <= 180
      ) {
        this.updateMapMarker(lat, lng);
      }
    },
    // Initialize Leaflet Map
    initMap(initLat, initLng) {
      if (!this.$refs.mapContainer) return;
      // Default center: Karnataka (Bangalore)
      let center = [12.9716, 77.5946];
      let zoom = 7;
      // Use provided lat/lng or form values
      const lat = initLat || parseFloat(this.form.latitude);
      const lng = initLng || parseFloat(this.form.longitude);
      if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
        center = [lat, lng];
        zoom = 15;
      }
      this.map = L.map(this.$refs.mapContainer).setView(center, zoom);
      // OpenStreetMap tile layer (free, no API key)
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 19,
      }).addTo(this.map);
      // Place marker if lat/lng exist
      if (zoom === 15) {
        this.placeMarker(center);
      }
      // Click on map to set location
      this.map.on("click", (e) => {
        if (this.isDisabled) return;
        const clickLat = e.latlng.lat;
        const clickLng = e.latlng.lng;
        this.form.latitude = clickLat.toFixed(8);
        this.form.longitude = clickLng.toFixed(8);
        this.placeMarker([clickLat, clickLng]);
        this.input();
      });
    },
    // Place or move marker on the map
    placeMarker(latlng) {
      if (!this.map) return;
      if (this.marker) {
        this.marker.setLatLng(latlng);
      } else {
        this.marker = L.marker(latlng, {
          draggable: !this.isDisabled,
        }).addTo(this.map);
        this.marker.bindPopup("Hospital Location").openPopup();
        // Drag end updates lat/lng
        this.marker.on("dragend", (e) => {
          const pos = e.target.getLatLng();
          this.form.latitude = pos.lat.toFixed(8);
          this.form.longitude = pos.lng.toFixed(8);
          this.input();
        });
      }
    },
    // Update marker and center map
    updateMapMarker(lat, lng) {
      if (!this.map) return;
      const latlng = [lat, lng];
      this.placeMarker(latlng);
      this.map.setView(latlng, Math.max(this.map.getZoom(), 15));
    },
    input() {
      this.$emit("input", this.form);
    },
  },
  async beforeMount() {
    this.form = this.value;
    // Load dropdowns sequentially to ensure each depends on the previous
    // Only load if value exists AND array is empty (prevents duplicate loads from watchers)
    if (this.form.country && this.states.length === 0) {
      await this.onCountryChange(true);
    }
    if (this.form.state && this.citys.length === 0) {
      await this.onStateChange(true);
    }
    if (this.form.city && this.taluks.length === 0) {
      await this.loadTaluks();
    }
    this.isInitialized = true;
    // Double-check all dropdowns are loaded after initialization (handles race conditions)
    this.$nextTick(async () => {
      // Ensure states are loaded if country is set
      if (this.form.country && this.states.length === 0) {
        await this.onCountryChange(true);
      }
      // Ensure districts are loaded if state is set
      if (this.form.state && this.citys.length === 0) {
        await this.onStateChange(true);
      }
      // Ensure taluks are loaded if district is set
      if (this.form.city && this.taluks.length === 0) {
        await this.loadTaluks();
      }
    });
  },
  mounted() {
    this.input();
  },
  beforeDestroy() {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  },
};
</script>

<style scoped>
.leaflet-map-container {
  width: 100%;
  height: 350px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  z-index: 0;
}
</style>
